/**
 * Centralized API client with proper error handling, timeout, retry, and type safety
 */

import type {
  Project,
  ProjectStatus,
  UploadImage,
  AssetPack,
  RenderJob,
  SceneImageSelection,
  SceneTextSelection,
  SceneTextElement,
  PerTemplateSettings,
} from '@/types/project'
import type { VideoTemplate } from '@/types/template'
import type { SessionCost, UsageData } from '@/types/provider'
import type { GlobalSettings, GlobalSettingsUpdate } from '@/types/globalSettings'
import type { DecorPart } from '@/types/decoration'

const API_BASE_URL = '/api'

// Default timeout and retry configuration
const DEFAULT_TIMEOUT = 30000 // 30 seconds
const DEFAULT_MAX_RETRIES = 3
const INITIAL_RETRY_DELAY = 1000 // 1 second

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown,
    /** サーバーが返す安定エラーコード（例: 'DUPLICATE_RENDER'）。文言判定の代わりに使う */
    public code?: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export class TimeoutError extends Error {
  constructor(message: string = 'Request timeout') {
    super(message)
    this.name = 'TimeoutError'
  }
}

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  _usage?: UsageData
}

interface FetchOptions extends RequestInit {
  timeout?: number
}

interface RetryOptions {
  maxRetries?: number
  retryDelay?: number
  shouldRetry?: (error: Error, attempt: number) => boolean
}

/**
 * Fetch with timeout support
 */
async function fetchWithTimeout(
  url: string,
  options: FetchOptions = {}
): Promise<Response> {
  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
      credentials: 'include', // Include cookies for session management
    })
    return response
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new TimeoutError(`Request timed out after ${timeout}ms`)
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Fetch with retry support and exponential backoff
 */
async function fetchWithRetry(
  url: string,
  options: FetchOptions = {},
  retryOptions: RetryOptions = {}
): Promise<Response> {
  const {
    maxRetries = DEFAULT_MAX_RETRIES,
    retryDelay = INITIAL_RETRY_DELAY,
    shouldRetry = (error, attempt) => {
      // 非冪等メソッド(POST/PATCH)はタイムアウト後に再送しない。タイムアウトは「サーバーが処理済みで
      // 応答だけ遅れた」場合があり、再送すると重複プロジェクト・二重レンダー・二重課金になる
      const method = (options.method ?? 'GET').toUpperCase()
      if (error instanceof TimeoutError && (method === 'POST' || method === 'PATCH')) {
        return false
      }
      // Don't retry on client errors (4xx) except 408 (Request Timeout) and 429 (Too Many Requests)
      if (error instanceof ApiError) {
        if (error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429) {
          return false
        }
      }
      // Retry on network errors and server errors
      return attempt < maxRetries
    },
  } = retryOptions

  let lastError: Error = new Error('Unknown error')

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchWithTimeout(url, options)
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err))

      // Check if we should retry
      if (!shouldRetry(lastError, attempt) || attempt === maxRetries) {
        throw lastError
      }

      // Exponential backoff: delay * 2^attempt (1s, 2s, 4s, ...)
      const delay = retryDelay * Math.pow(2, attempt)
      console.warn(`Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`, lastError.message)
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }

  throw lastError
}

async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  // Check HTTP status first
  if (!response.ok) {
    // Handle 401 Unauthorized - redirect to login
    if (response.status === 401) {
      // Avoid redirect loop for auth endpoints
      if (!window.location.pathname.startsWith('/login') &&
          !window.location.pathname.startsWith('/setup') &&
          !window.location.pathname.startsWith('/forgot-password') &&
          !window.location.pathname.startsWith('/reset-password') &&
          !window.location.pathname.startsWith('/signup') &&
          !window.location.pathname.startsWith('/pricing') &&
          !window.location.pathname.startsWith('/genemo') &&
          !window.location.pathname.startsWith('/terms') &&
          !window.location.pathname.startsWith('/privacy') &&
          window.location.pathname !== '/') {
        window.location.href = '/login'
      }
    }

    let errorMessage = `HTTP error: ${response.status} ${response.statusText}`
    let errorCode: string | undefined
    try {
      const errorData = await response.json()
      if (errorData.error) {
        errorMessage = errorData.error
      }
      if (typeof errorData.code === 'string') {
        errorCode = errorData.code
      }
    } catch {
      // Response is not JSON, use default message
    }
    throw new ApiError(errorMessage, response.status, undefined, errorCode)
  }

  // Parse JSON response
  try {
    const data = await response.json()
    return data as ApiResponse<T>
  } catch {
    throw new ApiError('Invalid JSON response', response.status)
  }
}

// Projects
export interface CreateProjectResponse {
  id: string
  status: ProjectStatus
}

export async function createProject(options?: { storyboardMode?: boolean }): Promise<ApiResponse<CreateProjectResponse>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(options ?? {}),
  })
  return handleResponse<CreateProjectResponse>(response)
}

export async function getProject(projectId: string): Promise<ApiResponse<Project>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/projects/${projectId}`)
  return handleResponse<Project>(response)
}

export async function updateProjectTemplateSettings(
  projectId: string,
  templateSettings: unknown
): Promise<ApiResponse<Project>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/projects/${projectId}/template-settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ templateSettings }),
  })
  return handleResponse<Project>(response)
}

// ===== 装飾SVGパーツ・ライブラリ =====
/** タグ/型でパーツを検索（実行時に文字/色/サイズを差し替えて使う）。 */
export async function searchDecorParts(
  params: { tags?: string[]; type?: string; limit?: number } = {}
): Promise<ApiResponse<{ total: number; count: number; parts: DecorPart[] }>> {
  const q = new URLSearchParams()
  if (params.tags?.length) q.set('tags', params.tags.join(','))
  if (params.type) q.set('type', params.type)
  if (params.limit) q.set('limit', String(params.limit))
  const response = await fetchWithRetry(`${API_BASE_URL}/decor-parts${q.toString() ? `?${q}` : ''}`)
  return handleResponse<{ total: number; count: number; parts: DecorPart[] }>(response)
}

/** フィルタUI用のタグ一覧（多い順）。 */
export async function getDecorTags(): Promise<ApiResponse<{ tag: string; count: number }[]>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/decor-parts/tags`)
  return handleResponse<{ tag: string; count: number }[]>(response)
}

/** 管理者用：全装飾パーツ（無効含む）＋無効ID一覧を取得。 */
export async function getAdminDecorParts(): Promise<ApiResponse<{ parts: DecorPart[]; disabledIds: string[] }>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/decor-parts/admin/all`)
  return handleResponse<{ parts: DecorPart[]; disabledIds: string[] }>(response)
}

/** 管理者用：無効化する装飾パーツ ID 一覧を保存（全ユーザーに影響）。 */
export async function updateAdminDecorDisabled(disabledIds: string[]): Promise<ApiResponse<{ disabledIds: string[] }>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/decor-parts/admin/disabled`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ disabledIds }),
  })
  return handleResponse<{ disabledIds: string[] }>(response)
}

/** 文字/色を差し替えたSVGのURL（<img src> でそのまま描画）。 */
export function decorPartSvgUrl(
  id: string,
  opts: { text?: Record<string, string>; colors?: Record<string, string> } = {}
): string {
  const q = new URLSearchParams()
  if (opts.text && Object.keys(opts.text).length) q.set('text', JSON.stringify(opts.text))
  if (opts.colors && Object.keys(opts.colors).length) q.set('colors', JSON.stringify(opts.colors))
  const s = q.toString()
  return `${API_BASE_URL}/decor-parts/${id}/svg${s ? `?${s}` : ''}`
}

/** 文字/色を差し替えたHTMLパーツのURL（container-type箱に入れて幅を与えると cqw でスケール）。 */
export function decorPartHtmlUrl(
  id: string,
  opts: { text?: Record<string, string>; colors?: Record<string, string> } = {}
): string {
  const q = new URLSearchParams()
  if (opts.text && Object.keys(opts.text).length) q.set('text', JSON.stringify(opts.text))
  if (opts.colors && Object.keys(opts.colors).length) q.set('colors', JSON.stringify(opts.colors))
  const s = q.toString()
  return `${API_BASE_URL}/decor-parts/${id}/html${s ? `?${s}` : ''}`
}

export async function updatePerTemplateSettings(
  projectId: string,
  perTemplateSettings: PerTemplateSettings
): Promise<ApiResponse<Project>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/projects/${projectId}/per-template-settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ perTemplateSettings }),
  })
  return handleResponse<Project>(response)
}

export async function updateProject(
  projectId: string,
  updates: { selectedTemplate?: string; name?: string; tastePresetId?: string | null }
): Promise<ApiResponse<Project>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/projects/${projectId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  })
  return handleResponse<Project>(response)
}

// Images
export interface UploadImagesResponse {
  images: UploadImage[]
  count: number
}

export async function uploadImages(
  projectId: string,
  formData: FormData
): Promise<ApiResponse<UploadImagesResponse>> {
  // Longer timeout for file uploads, fewer retries
  const response = await fetchWithRetry(
    `${API_BASE_URL}/projects/${projectId}/images`,
    { method: 'POST', body: formData, timeout: 120000 },
    { maxRetries: 1 }
  )
  return handleResponse<UploadImagesResponse>(response)
}

// Analysis
export interface StartAnalysisResponse {
  projectId: string
  status: string
}

export async function startAnalysis(
  projectId: string,
  options?: { analysisModel?: string; generationModel?: string; preInfo?: import('@/types/project').ProductPreInfo }
): Promise<ApiResponse<StartAnalysisResponse>> {
  // Analysis can take a while to start, give it more time
  const response = await fetchWithRetry(
    `${API_BASE_URL}/projects/${projectId}/analyze`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options || {}),
      timeout: 60000,
    },
    { maxRetries: 2 }
  )
  return handleResponse<StartAnalysisResponse>(response)
}

export interface AnalysisStatusResponse {
  projectStatus: ProjectStatus
  status: 'idle' | 'processing' | 'completed' | 'error'
  step: string
  progress: number
  message: string
  hasAssetPack: boolean
  error?: string
}

export async function getAnalysisStatus(
  projectId: string
): Promise<ApiResponse<AnalysisStatusResponse>> {
  // Quick status check, short timeout
  const response = await fetchWithRetry(
    `${API_BASE_URL}/projects/${projectId}/analyze`,
    { timeout: 10000 },
    { maxRetries: 2 }
  )
  return handleResponse<AnalysisStatusResponse>(response)
}

// Assets
export async function getAssets(projectId: string): Promise<ApiResponse<AssetPack>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/projects/${projectId}/assets`)
  return handleResponse<AssetPack>(response)
}

export interface UpdateAssetSelectionRequest {
  headlines?: number[]
  sellingPoints?: number[]
  cta?: number
  imageOrder?: string[]
  sceneImageIds?: SceneImageSelection
  sceneTexts?: SceneTextSelection
  sceneTextElements?: Partial<Record<string, SceneTextElement[]>>
  sceneVideoEnabled?: Partial<Record<string, boolean>>
  sceneCaptionEnabled?: Partial<Record<string, boolean>>
  sceneSfxIds?: Partial<Record<string, string>>
  sceneSfxVolumes?: Partial<Record<string, number>>
  templateSelections?: Partial<Record<string, { templateId?: string; sceneImageIds?: SceneImageSelection; sceneTexts?: SceneTextSelection; sceneTextElements?: Partial<Record<string, SceneTextElement[]>>; sceneSettings?: Partial<Record<string, any>>; sceneVideoEnabled?: Partial<Record<string, boolean>>; sceneCaptionEnabled?: Partial<Record<string, boolean>>; sceneSfxIds?: Partial<Record<string, string>>; sceneSfxVolumes?: Partial<Record<string, number>> }>>
  musicId?: string
  musicVolume?: number
}

export async function updateAssetSelection(
  projectId: string,
  selection: UpdateAssetSelectionRequest
): Promise<ApiResponse<AssetPack>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/projects/${projectId}/assets`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ selected: selection }),
  })
  return handleResponse<AssetPack>(response)
}

// Templates
export async function getTemplates(): Promise<ApiResponse<VideoTemplate[]>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/templates`)
  return handleResponse<VideoTemplate[]>(response)
}

export async function getTemplate(templateId: string): Promise<ApiResponse<VideoTemplate>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/templates/${templateId}`)
  return handleResponse<VideoTemplate>(response)
}

// Videos
export interface StartRenderResponse {
  jobId: string
  projectId: string
  templateId: string
  aspectRatio: string
  status: string
}

export async function startRender(
  projectId: string,
  templateId: string,
  aspectRatio: '9:16' | '16:9' | '1:1' | '4:5',
  customizations?: Record<string, unknown>
): Promise<ApiResponse<StartRenderResponse>> {
  const response = await fetchWithRetry(
    `${API_BASE_URL}/projects/${projectId}/videos`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ templateId, aspectRatio, customizations }),
      timeout: 60000,
    },
    { maxRetries: 2 }
  )
  return handleResponse<StartRenderResponse>(response)
}

export interface RenderJobStatusResponse {
  id: string
  status: 'queued' | 'pending' | 'processing' | 'completed' | 'failed'
  progress: number
  outputPath?: string
  downloadUrl?: string
  error?: string
  queuePosition?: number
}

export async function getRenderJobStatus(
  projectId: string,
  jobId: string
): Promise<ApiResponse<RenderJobStatusResponse>> {
  const params = new URLSearchParams({ projectId })
  // Short timeout for status checks
  const response = await fetchWithRetry(
    `${API_BASE_URL}/render-jobs/${encodeURIComponent(jobId)}?${params}`,
    { timeout: 10000 },
    { maxRetries: 2 }
  )
  return handleResponse<RenderJobStatusResponse>(response)
}

// Extended RenderJob with download URL (returned by videos endpoint)
export interface RenderJobWithUrl extends RenderJob {
  downloadUrl: string | null
}

export async function getRenderedVideos(
  projectId: string
): Promise<ApiResponse<RenderJobWithUrl[]>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/projects/${projectId}/videos`)
  return handleResponse<RenderJobWithUrl[]>(response)
}

// Usage
export async function getUsageSummary(projectId?: string): Promise<ApiResponse<SessionCost>> {
  const params = new URLSearchParams()
  if (projectId) {
    params.set('projectId', projectId)
  }
  const query = params.toString()
  const url = query ? `${API_BASE_URL}/usage?${query}` : `${API_BASE_URL}/usage`
  const response = await fetchWithRetry(url)
  return handleResponse<SessionCost>(response)
}

export async function getUsageHistory(projectId?: string): Promise<ApiResponse<UsageData[]>> {
  const params = new URLSearchParams()
  if (projectId) {
    params.set('projectId', projectId)
  }
  const query = params.toString()
  const url = query ? `${API_BASE_URL}/usage/history?${query}` : `${API_BASE_URL}/usage/history`
  const response = await fetchWithRetry(url)
  return handleResponse<UsageData[]>(response)
}

export interface ClearUsageResponse {
  message: string
}

export async function clearUsage(): Promise<ApiResponse<ClearUsageResponse>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/usage/clear`, {
    method: 'POST',
  })
  return handleResponse<ClearUsageResponse>(response)
}

// Settings API
export async function getSettings(): Promise<ApiResponse<any>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/settings`)
  return handleResponse<any>(response)
}

// Global Settings API
export async function getGlobalSettings(): Promise<ApiResponse<GlobalSettings>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/global-settings`)
  return handleResponse<GlobalSettings>(response)
}

export async function updateGlobalSettings(
  update: GlobalSettingsUpdate
): Promise<ApiResponse<GlobalSettings>> {
  const response = await fetchWithRetry(`${API_BASE_URL}/global-settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(update),
  })
  return handleResponse<GlobalSettings>(response)
}

// Polling helper
export async function pollUntilComplete<T>(
  pollFn: () => Promise<ApiResponse<T>>,
  isComplete: (data: T) => boolean,
  options: {
    interval?: number
    maxAttempts?: number
    onProgress?: (data: T) => void
  } = {}
): Promise<T> {
  const { interval = 1000, maxAttempts = 120, onProgress } = options
  let attempts = 0

  while (attempts < maxAttempts) {
    const result = await pollFn()

    if (!result.success) {
      throw new ApiError(result.error || 'Polling failed', 500)
    }

    if (onProgress && result.data) {
      onProgress(result.data)
    }

    if (result.data && isComplete(result.data)) {
      return result.data
    }

    attempts++
    await new Promise((resolve) => setTimeout(resolve, interval))
  }

  throw new ApiError('Polling timeout', 408)
}

// ============================================================================
// Generic API Client
// ============================================================================

/**
 * Generic API client for making authenticated requests
 */
export const apiClient = {
  async get<T = unknown>(endpoint: string): Promise<ApiResponse<T>> {
    const response = await fetchWithRetry(`${API_BASE_URL}${endpoint}`)
    return handleResponse<T>(response)
  },

  async post<T = unknown>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    const response = await fetchWithRetry(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: data !== undefined ? JSON.stringify(data) : undefined,
    })
    return handleResponse<T>(response)
  },

  async put<T = unknown>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    const response = await fetchWithRetry(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: data !== undefined ? JSON.stringify(data) : undefined,
    })
    return handleResponse<T>(response)
  },

  async delete<T = unknown>(endpoint: string): Promise<ApiResponse<T>> {
    const response = await fetchWithRetry(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
    })
    return handleResponse<T>(response)
  },
}
